import React, { useState, useEffect, useCallback} from 'react';
import Layout from '../components/Layout';
import { Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { financeAssignmentsAPI, financeAPI, companyAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';

const FinanceAssignments = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('finance_assignments');
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || 'company-code-cca';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  
  // Master data
  const [companyCodes, setCompanyCodes] = useState([]);
  const [creditControlAreas, setCreditControlAreas] = useState([]);
  const [businessAreas, setBusinessAreas] = useState([]);
  const [consolidatedBusinessAreas, setConsolidatedBusinessAreas] = useState([]);
  const [functionalAreas, setFunctionalAreas] = useState([]);
  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [fiscalYearVariants, setFiscalYearVariants] = useState([]);

  // No filter selections needed - load all assignments

  // Assignments data
  const [assignments, setAssignments] = useState([]);
  
  // New assignment selections
  const [newCompanyCode, setNewCompanyCode] = useState('');
  const [newCCA, setNewCCA] = useState('');
  const [newBA, setNewBA] = useState('');
  const [newCBA, setNewCBA] = useState('');
  const [newFA, setNewFA] = useState('');
  const [newCOA, setNewCOA] = useState('');
  const [newFYV, setNewFYV] = useState('');
  
  // GL Account Extensions state
  const [selectedCompanyCodeForGL, setSelectedCompanyCodeForGL] = useState('');
  const [glAccountsData, setGLAccountsData] = useState(null);
  const [isExtensionDialogOpen, setIsExtensionDialogOpen] = useState(false);
  const [editingExtension, setEditingExtension] = useState(null);

  useEffect(() => {
    setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  useEffect(() => {
    loadMasterData();
  }, []);


  const loadMasterData = async () => {
    try {
      const [ccResponse, ccaResponse, baResponse, cbaResponse, faResponse, coaResponse, fyvResponse] = await Promise.all([
        companyAPI.getCompanyCodes(),
        financeAPI.getCreditControlAreas(),
        financeAPI.getBusinessAreas(),
        financeAPI.getConsolidatedBusinessAreas(),
        financeAPI.getFunctionalAreas(),
        financeAPI.getChartOfAccounts(),
        financeAPI.getFiscalYearVariants(),
      ]);
      setCompanyCodes(ccResponse.data);
      setCreditControlAreas(ccaResponse.data);
      setBusinessAreas(baResponse.data);
      setConsolidatedBusinessAreas(cbaResponse.data);
      setFunctionalAreas(faResponse.data);
      setChartOfAccounts(coaResponse.data);
      setFiscalYearVariants(fyvResponse.data);
    } catch (error) {
      toast.error('Failed to load master data');
    }
  };

  const loadAssignments = useCallback(async () => {
    try {
      let response;
      switch (activeTab) {
        case 'company-code-cca':
          response = await financeAssignmentsAPI.getCompanyCodeCCAAssignments('', '');
          break;
        case 'business-area-cba':
          response = await financeAssignmentsAPI.getBusinessAreaCBAAssignments('', '');
          break;
        case 'company-code-fa':
          response = await financeAssignmentsAPI.getCompanyCodeFAAssignments('', '');
          break;
        case 'company-code-coa':
          response = await financeAssignmentsAPI.getCompanyCodeCOAAssignments();
          break;
        case 'company-code-fyv':
          response = await financeAssignmentsAPI.getCompanyCodeFYVAssignments();
          break;
        case 'gl-account-extensions':
          // For GL extensions, we load per company code selection
          return;
      }
      setAssignments(response.data);
    } catch (error) {
      toast.error('Failed to load assignments');
    }
  }, [activeTab]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const handleAddAssignment = async () => {
    try {
      let assignmentData;
      switch (activeTab) {
        case 'company-code-cca':
          if (!newCompanyCode || !newCCA) {
            toast.error('Please select both Company Code and Credit Control Area');
            return;
          }
          assignmentData = {
            company_code_id: newCompanyCode,
            credit_control_area: newCCA
          };
          await financeAssignmentsAPI.createCompanyCodeCCAAssignment(assignmentData);
          break;
        case 'business-area-cba':
          if (!newBA || !newCBA) {
            toast.error('Please select both Business Area and Consolidated Business Area');
            return;
          }
          assignmentData = {
            business_area: newBA,
            consolidated_business_area: newCBA
          };
          await financeAssignmentsAPI.createBusinessAreaCBAAssignment(assignmentData);
          break;
        case 'company-code-fa':
          if (!newCompanyCode || !newFA) {
            toast.error('Please select both Company Code and Functional Area');
            return;
          }
          assignmentData = {
            company_code_id: newCompanyCode,
            functional_area: newFA
          };
          await financeAssignmentsAPI.createCompanyCodeFAAssignment(assignmentData);
          break;
        case 'company-code-coa':
          if (!newCompanyCode || !newCOA) {
            toast.error('Please select both Company Code and Chart of Accounts');
            return;
          }
          assignmentData = {
            company_code_id: newCompanyCode,
            coa_id: newCOA
          };
          await financeAssignmentsAPI.createCompanyCodeCOAAssignment(assignmentData);
          break;
        case 'company-code-fyv':
          if (!newCompanyCode || !newFYV) {
            toast.error('Please select both Company Code and Fiscal Year Variant');
            return;
          }
          assignmentData = {
            company_code_id: newCompanyCode,
            fiscal_year_variant_id: newFYV
          };
          const fyvResult = await financeAssignmentsAPI.createCompanyCodeFYVAssignment(assignmentData);
          if (fyvResult.data.warnings && fyvResult.data.warnings.length > 0) {
            toast.warning(fyvResult.data.warnings.join('\n'));
          }
          break;
      }
      
      // Reset selections
      setNewCompanyCode('');
      setNewCCA('');
      setNewBA('');
      setNewCBA('');
      setNewFA('');
      setNewCOA('');
      setNewFYV('');
      
      loadAssignments();
      toast.success('Assignment created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create assignment');
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;

    try {
      switch (activeTab) {
        case 'company-code-cca':
          await financeAssignmentsAPI.deleteCompanyCodeCCAAssignment(assignmentId);
          break;
        case 'business-area-cba':
          await financeAssignmentsAPI.deleteBusinessAreaCBAAssignment(assignmentId);
          break;
        case 'company-code-fa':
          await financeAssignmentsAPI.deleteCompanyCodeFAAssignment(assignmentId);
          break;
        case 'company-code-coa':
          await financeAssignmentsAPI.deleteCompanyCodeCOAAssignment(assignmentId);
          break;
        case 'company-code-fyv':
          await financeAssignmentsAPI.deleteCompanyCodeFYVAssignment(assignmentId);
          break;
      }
      loadAssignments();
      toast.success('Assignment deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete assignment');
    }
  };

  const tabs = [
    { id: 'company-code-cca', label: 'Company Code → Credit Control Area' },
    { id: 'business-area-cba', label: 'Business Area → Consolidated Business Area' },
    { id: 'company-code-fa', label: 'Company Code → Functional Area' },
    { id: 'company-code-coa', label: 'Company Code → COA' },
    { id: 'company-code-fyv', label: 'Company Code → FYV' },
    { id: 'gl-account-extensions', label: 'GL Account Extensions' },
  ];

  // GL Account Extensions Handlers
  const loadGLAccountsForCompanyCode = async (companyCodeId) => {
    if (!companyCodeId) {
      setGLAccountsData(null);
      return;
    }
    try {
      const response = await financeAssignmentsAPI.getGLAccountsForCompanyCode(companyCodeId);
      setGLAccountsData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load GL accounts');
      setGLAccountsData(null);
    }
  };

  const handleCompanyCodeSelect = (companyCodeId) => {
    setSelectedCompanyCodeForGL(companyCodeId);
    loadGLAccountsForCompanyCode(companyCodeId);
  };

  const handleActivateAccount = async (account) => {
    if (!glAccountsData || !glAccountsData.coa_id) {
      toast.error('Company Code must be assigned to a COA first');
      return;
    }

    try {
      const extensionData = {
        company_code_id: selectedCompanyCodeForGL,
        coa_id: glAccountsData.coa_id,
        account_number: account.account_number,
        is_active: true,
        local_currency: '',
        tax_code: '',
        field_status: '',
        posting_blocked: false,
        reconciliation_account: '',
        sort_key: '',
        line_item_display: true,
        open_item_management: false
      };
      
      await financeAssignmentsAPI.createGLAccountExtension(extensionData);
      toast.success('GL Account activated successfully');
      loadGLAccountsForCompanyCode(selectedCompanyCodeForGL);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate account');
    }
  };

  const handleEditExtension = (account) => {
    setEditingExtension(account);
    setIsExtensionDialogOpen(true);
  };

  const handleUpdateExtension = async (extensionData) => {
    try {
      await financeAssignmentsAPI.updateGLAccountExtension(
        editingExtension.extension_id,
        extensionData
      );
      toast.success('Extension updated successfully');
      setIsExtensionDialogOpen(false);
      setEditingExtension(null);
      loadGLAccountsForCompanyCode(selectedCompanyCodeForGL);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update extension');
    }
  };

  const handleDeactivateAccount = async (account) => {
    if (!account.extension_id) return;
    
    if (!window.confirm(`Deactivate GL Account ${account.account_number}?`)) return;

    try {
      await financeAssignmentsAPI.deleteGLAccountExtension(account.extension_id);
      toast.success('GL Account deactivated successfully');
      loadGLAccountsForCompanyCode(selectedCompanyCodeForGL);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate account');
    }
  };

  // Filter section removed - showing all assignments automatically

  const renderAddForm = () => {
    switch (activeTab) {
      case 'company-code-cca':
        return (
          <>
            <div className="flex-1">
              <Select value={newCompanyCode} onValueChange={setNewCompanyCode}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Company Code" />
                </SelectTrigger>
                <SelectContent>
                  {companyCodes.map(cc => (
                    <SelectItem key={cc.company_code_id} value={cc.company_code_id}>
                      {cc.company_code_id} - {cc.company_code_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Select value={newCCA} onValueChange={setNewCCA}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Credit Control Area" />
                </SelectTrigger>
                <SelectContent>
                  {creditControlAreas.map(cca => (
                    <SelectItem key={cca.credit_control_area} value={cca.credit_control_area}>
                      {cca.credit_control_area} - {cca.currency}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        );
      case 'business-area-cba':
        return (
          <>
            <div className="flex-1">
              <Select value={newBA} onValueChange={setNewBA}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Business Area" />
                </SelectTrigger>
                <SelectContent>
                  {businessAreas.map(ba => (
                    <SelectItem key={ba.business_area} value={ba.business_area}>
                      {ba.business_area} - {ba.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Select value={newCBA} onValueChange={setNewCBA}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Consolidated Business Area" />
                </SelectTrigger>
                <SelectContent>
                  {consolidatedBusinessAreas.map(cba => (
                    <SelectItem key={cba.consolidated_business_area} value={cba.consolidated_business_area}>
                      {cba.consolidated_business_area} - {cba.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        );
      case 'company-code-fa':
        return (
          <>
            <div className="flex-1">
              <Select value={newCompanyCode} onValueChange={setNewCompanyCode}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Company Code" />
                </SelectTrigger>
                <SelectContent>
                  {companyCodes.map(cc => (
                    <SelectItem key={cc.company_code_id} value={cc.company_code_id}>
                      {cc.company_code_id} - {cc.company_code_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Select value={newFA} onValueChange={setNewFA}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Functional Area" />
                </SelectTrigger>
                <SelectContent>
                  {functionalAreas.map(fa => (
                    <SelectItem key={fa.functional_area} value={fa.functional_area}>
                      {fa.functional_area} - {fa.area_text}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        );
      case 'company-code-coa':
        return (
          <>
            <div className="flex-1">
              <Select value={newCompanyCode} onValueChange={setNewCompanyCode}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Company Code" />
                </SelectTrigger>
                <SelectContent>
                  {companyCodes.map(cc => (
                    <SelectItem key={cc.company_code_id} value={cc.company_code_id}>
                      {cc.company_code_id} - {cc.company_code_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Select value={newCOA} onValueChange={setNewCOA}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Chart of Accounts" />
                </SelectTrigger>
                <SelectContent>
                  {chartOfAccounts.map(coa => (
                    <SelectItem key={coa.coa_id} value={coa.coa_id}>
                      {coa.coa_id} - {coa.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        );
      case 'company-code-fyv':
        return (
          <>
            <div className="flex-1">
              <Select value={newCompanyCode} onValueChange={setNewCompanyCode}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Company Code" />
                </SelectTrigger>
                <SelectContent>
                  {companyCodes.map(cc => (
                    <SelectItem key={cc.company_code_id} value={cc.company_code_id}>
                      {cc.company_code_id} - {cc.company_code_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Select value={newFYV} onValueChange={setNewFYV}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Fiscal Year Variant" />
                </SelectTrigger>
                <SelectContent>
                  {fiscalYearVariants.map(fyv => (
                    <SelectItem key={fyv.fiscal_year_variant_id} value={fyv.fiscal_year_variant_id}>
                      {fyv.fiscal_year_variant_id} - {fyv.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        );
      case 'gl-account-extensions':
        return null; // GL extensions have special UI
    }
  };

  const renderTableHeaders = () => {
    switch (activeTab) {
      case 'company-code-cca':
        return (
          <>
            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Company Code</th>
            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Credit Control Area</th>
            <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
          </>
        );
      case 'business-area-cba':
        return (
          <>
            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Business Area</th>
            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Consolidated Business Area</th>
            <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
          </>
        );
      case 'company-code-fa':
        return (
          <>
            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Company Code</th>
            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Functional Area</th>
            <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
          </>
        );
      case 'company-code-coa':
        return (
          <>
            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Company Code</th>
            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Chart of Accounts</th>
            <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
          </>
        );
      case 'company-code-fyv':
        return (
          <>
            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Company Code</th>
            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Fiscal Year Variant</th>
            <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
          </>
        );
    }
  };

  const renderTableRow = (assignment) => {
    switch (activeTab) {
      case 'company-code-cca':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm">{assignment.company_code_id}</td>
            <td className="py-3 px-4 font-mono text-sm">{assignment.credit_control_area}</td>
          </>
        );
      case 'business-area-cba':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm">{assignment.business_area}</td>
            <td className="py-3 px-4 font-mono text-sm">{assignment.consolidated_business_area}</td>
          </>
        );
      case 'company-code-fa':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm">{assignment.company_code_id}</td>
            <td className="py-3 px-4 font-mono text-sm">{assignment.functional_area}</td>
          </>
        );
      case 'company-code-coa':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm">{assignment.company_code_id}</td>
            <td className="py-3 px-4 font-mono text-sm">{assignment.coa_id}</td>
          </>
        );
      case 'company-code-fyv':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm">{assignment.company_code_id}</td>
            <td className="py-3 px-4 font-mono text-sm">{assignment.fiscal_year_variant_id}</td>
          </>
        );
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Finance Assignments</h2>
          <p className="text-sm text-gray-500 mt-1">Manage finance module entity assignments</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setActiveTab(tab.id);
                  setAssignments([]);
                }}
                className={`px-6 py-4 font-medium whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-green-600 border-b-2 border-green-600'
                    : 'text-gray-600'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Add Assignment Section - Only for standard assignments */}
        {activeTab !== 'gl-account-extensions' && (
          <Card className="p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Assignment</h3>
            <div className="flex gap-4 items-end">
              {renderAddForm()}
              {writable && (<Button onClick={handleAddAssignment}>
                <Plus className="w-4 h-4 mr-2" />
                Add
              </Button>)}
            </div>
          </Card>
        )}

        {/* GL Account Extensions Special UI */}
        {activeTab === 'gl-account-extensions' && (
          <Card className="p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Company Code</h3>
            <div className="max-w-md">
              <Select value={selectedCompanyCodeForGL} onValueChange={handleCompanyCodeSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Company Code to manage GL accounts" />
                </SelectTrigger>
                <SelectContent>
                  {companyCodes.map(cc => (
                    <SelectItem key={cc.company_code_id} value={cc.company_code_id}>
                      {cc.company_code_id} - {cc.company_code_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {glAccountsData && !glAccountsData.coa_id && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ Company Code {glAccountsData.company_code_id} is not assigned to any COA. 
                  Please assign a COA first in the "Company Code → COA" tab.
                </p>
              </div>
            )}
          </Card>
        )}

        {/* Standard Assignments Table */}
        {activeTab !== 'gl-account-extensions' && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {tabs.find(t => t.id === activeTab)?.label}
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    {renderTableHeaders()}
                  </tr>
                </thead>
                <tbody>
                  {assignments.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center py-8 text-gray-500">
                        No assignments found
                      </td>
                    </tr>
                  ) : (
                    assignments.map((assignment) => (
                      <tr key={assignment.assignment_id} className="border-b border-gray-100 hover:bg-gray-50">
                        {renderTableRow(assignment)}
                        <td className="py-3 px-4 text-right">
                          {writable && (<Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteAssignment(assignment.assignment_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* GL Account Extensions Table */}
        {activeTab === 'gl-account-extensions' && glAccountsData && glAccountsData.coa_id && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              GL Accounts from COA {glAccountsData.coa_id}
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Account #</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Account Name</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Type</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Group</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Status</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Posting</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {glAccountsData.accounts.map((account) => (
                    <tr key={account.account_number} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 font-mono font-semibold">{account.account_number}</td>
                      <td className="py-3 px-4">
                        <div>{account.account_name}</div>
                        {account.short_text && (
                          <div className="text-xs text-gray-500">{account.short_text}</div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded">
                          {account.account_type}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-mono text-xs">{account.account_group}</td>
                      <td className="py-3 px-4 text-center">
                        {account.is_active ? (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded font-semibold">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {account.posting_blocked ? (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">
                            Blocked
                          </span>
                        ) : account.allow_postings ? (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                            Allowed
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded">
                            No Post
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex gap-2 justify-end">
                          {!account.is_extended ? (
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => handleActivateAccount(account)}
                            >
                              Activate
                            </Button>
                          ) : (
                            <>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditExtension(account)}
                              >
                                Edit
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeactivateAccount(account)}
                              >
                                Deactivate
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default FinanceAssignments;
